import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import LoadingSpinner from "../../components/LoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ForecastFollowUpFilters from "../../components/gridCost/ForecastFollowUpFilters";
import ForecastActualChart from "../../components/gridCost/ForecastActualChart";
import { FormCard } from "../../components/FormCard";
import { useAuth } from "../../providers/AuthContext";
import { getForecastVsActual } from "../../api/Telemetry";
import {
    faBolt,
    faWavePulse,
    faArrowRightArrowLeft,
    faPercent,
} from "@fortawesome/pro-regular-svg-icons";

function formatKwh(value) {
    if (value == null || !Number.isFinite(Number(value))) {
        return "—";
    }
    return `${Number(value).toFixed(1)} kWh`;
}

function formatPercent(value) {
    if (value == null || !Number.isFinite(Number(value))) {
        return "0.0%";
    }
    const sign = Number(value) > 0 ? "+" : "";
    return `${sign}${Number(value).toFixed(1)}%`;
}

function summarizeSeries(series) {
    const rows = Array.isArray(series) ? series : [];

    let totalForecast = 0;
    let totalActual = 0;

    for (const row of rows) {
        if (row?.forecastKwh != null && Number.isFinite(Number(row.forecastKwh))) {
            totalForecast += Number(row.forecastKwh);
        }
        if (row?.actualKwh != null && Number.isFinite(Number(row.actualKwh))) {
            totalActual += Number(row.actualKwh);
        }
    }

    const deltaKwh = totalActual - totalForecast;
    const deltaPercent = totalForecast > 0 ? (deltaKwh / totalForecast) * 100 : 0;

    return { totalForecast, totalActual, deltaKwh, deltaPercent };
}

function buildCollapsedSummary({ totalForecast, totalActual, deltaKwh, deltaPercent }, t) {
    return t("forecast.collapsedSummary", {
        forecast: formatKwh(totalForecast),
        actual: formatKwh(totalActual),
        diff: formatKwh(deltaKwh),
        percent: formatPercent(deltaPercent),
    });
}

const STAT_ICON_TINTS = [
    "bg-primary-50 text-primary-500",
    "bg-secondary-50 text-secondary-500",
    "bg-warning-50 text-warning-500",
    "bg-teal-50 text-teal-500",
];

function StatCard({ icon, title, value, iconTintIndex = 0 }) {
    const tint = STAT_ICON_TINTS[iconTintIndex % STAT_ICON_TINTS.length];

    return (
        <div className="flex items-center gap-2.5 rounded-xl border border-mute-100 bg-white p-3 shadow-md">
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${tint}`}>
                <FontAwesomeIcon icon={icon} className="text-sm" />
            </div>
            <div className="flex min-w-0 flex-col">
                <p className="m-0 text-xs font-normal text-mute-600">{title}</p>
                <p className="m-0 text-base font-bold tabular-nums text-black">{value}</p>
            </div>
        </div>
    );
}

function StatCards({ totalForecast, totalActual, deltaKwh, deltaPercent, t }) {
    return (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 mb-5">
            <StatCard
                icon={faWavePulse}
                title={t("forecast.totalForecast")}
                value={formatKwh(totalForecast)}
                iconTintIndex={0}
            />
            <StatCard
                icon={faBolt}
                title={t("forecast.totalActual")}
                value={formatKwh(totalActual)}
                iconTintIndex={3}
            />
            <StatCard
                icon={faArrowRightArrowLeft}
                title={t("forecast.delta")}
                value={formatKwh(deltaKwh)}
                iconTintIndex={2}
            />
            <StatCard
                icon={faPercent}
                title={t("forecast.deltaPercent")}
                value={formatPercent(deltaPercent)}
                iconTintIndex={1}
            />
        </div>
    );
}

export default function ForecastFollowUpPage() {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const [filters, setFilters] = useState({
        isValid: false,
        biddingZonePublicId: "",
        sitePublicIds: [],
        startDateTime: null,
        endDateTime: null,
    });
    const [loadingData, setLoadingData] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [result, setResult] = useState(null);
    const [fetchError, setFetchError] = useState(null);

    const filterKey = [
        filters.biddingZonePublicId,
        (filters.sitePublicIds || []).join("|"),
        filters.startDateTime,
        filters.endDateTime,
    ].join(";");

    useEffect(() => {
        setResult(null);
        setFetchError(null);
    }, [filterKey]);

    const handleFetch = async () => {
        if (!filters.isValid) {
            toast.error(
                filters.dateRangeError ||
                filters.siteSelectionError ||
                filters.siteLimitError ||
                t("forecast.selectSiteAndDates")
            );
            return;
        }

        setFetching(true);
        setResult(null);
        setFetchError(null);

        try {
            const token = await getAccessToken();
            const payload = {
                sitePublicIds: filters.sitePublicIds,
                startDateTime: filters.startDateTime,
                endDateTime: filters.endDateTime,
            };

            if (filters.biddingZonePublicId) {
                payload.biddingZonePublicId = filters.biddingZonePublicId;
            }

            const data = await getForecastVsActual(token, payload);
            setResult(data);
            toast.success(t("forecast.loaded"));
        } catch (err) {
            setFetchError(err.message);
            toast.error(err.message);
        } finally {
            setFetching(false);
        }
    };

    const summary = result?.summary;
    const showAggregated = Boolean(filters.aggregateByBiddingZone && result?.biddingZonePublicId);

    return (
        <>
            <HeaderBar />
            <div className="flex w-full min-h-0">
                <Sidebar />
                <main className="page">
                    <div className="page-content">
                        <h2 className="mb-2">{t("pages.forecastFollowUp")}</h2>
                        <p className="text-sm text-mute-600 mb-4">
                            {t("forecast.intro")}
                        </p>

                        <ForecastFollowUpFilters
                            onFiltersChange={setFilters}
                            onLoadingChange={setLoadingData}
                            onFetch={handleFetch}
                            fetching={fetching}
                            loadingData={loadingData}
                        />

                        {loadingData ? (
                            <LoadingSpinner text={t("forecast.loadingSitesZones")} className="mt-4" />
                        ) : fetching ? (
                            <LoadingSpinner text={t("forecast.loadingForecast")} className="mt-4" />
                        ) : fetchError ? (
                            <div
                                className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                                role="alert"
                            >
                                {fetchError}
                            </div>
                        ) : result ? (
                            <div className="mt-6 space-y-6" key={filterKey}>
                                {showAggregated ? (
                                    <FormCard
                                        title={t("forecast.aggregatedTitle", {
                                            zone: result.biddingZoneName || t("forecast.aggregatedFallback"),
                                        })}
                                        description={t("forecast.aggregatedDescription")}
                                        summary={
                                            summary
                                                ? buildCollapsedSummary({
                                                      totalForecast: summary.totalForecastKwh,
                                                      totalActual: summary.totalActualKwh,
                                                      deltaKwh: summary.deltaKwh,
                                                      deltaPercent: summary.deltaPercent,
                                                  }, t)
                                                : undefined
                                        }
                                        collapsible
                                        defaultOpen
                                        titleClassName="text-lg"
                                        headerClassName="mb-3"
                                    >
                                        {summary ? (
                                            <StatCards
                                                totalForecast={summary.totalForecastKwh}
                                                totalActual={summary.totalActualKwh}
                                                deltaKwh={summary.deltaKwh}
                                                deltaPercent={summary.deltaPercent}
                                                t={t}
                                            />
                                        ) : null}

                                        <ForecastActualChart
                                            rows={result.aggregatedSeries || []}
                                            title={t("forecast.chartTitle")}
                                        />
                                    </FormCard>
                                ) : null}

                                {(result.sites || []).map((site) => {
                                    const siteSummary = summarizeSeries(site.series);

                                    return (
                                        <FormCard
                                            key={site.sitePublicId}
                                            title={site.siteName || site.sitePublicId}
                                            description={t("gridCost.siteId", { id: site.sitePublicId })}
                                            summary={buildCollapsedSummary(siteSummary, t)}
                                            collapsible
                                            defaultOpen
                                            titleClassName="text-lg"
                                            headerClassName="mb-3"
                                        >
                                            <StatCards
                                                totalForecast={siteSummary.totalForecast}
                                                totalActual={siteSummary.totalActual}
                                                deltaKwh={siteSummary.deltaKwh}
                                                deltaPercent={siteSummary.deltaPercent}
                                                t={t}
                                            />

                                            <ForecastActualChart
                                                rows={site.series || []}
                                                title={t("forecast.chartTitle")}
                                            />
                                        </FormCard>
                                    );
                                })}
                            </div>
                        ) : null}
                    </div>
                </main>
            </div>
        </>
    );
}
